// frontend/src/pages/auth/ForgotPassword.tsx
import React, { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Heart, Mail } from 'react-feather';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import Card from '../../components/common/Card';
import Alert from '../../components/common/Alert';
import api from '../../services/api';
import { toast } from 'react-toastify';

const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (!email) {
      setError('Please enter your email address');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      await api.post('/auth/forgot-password', { email });
      
      setSuccess(true);
      toast.success('Password reset instructions have been sent to your email');
    } catch (err: any) {
      console.error('Forgot password error:', err);
      const errorMessage = err.response?.data?.message || 'Failed to send reset email';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <Heart className="h-12 w-12 text-pink-500" />
        </div>
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          Reset your password
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          We'll send you instructions to reset your password
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <Card>
          {success ? (
            <div className="px-4 py-5 sm:p-6">
              <div className="text-center">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                  <Mail className="h-6 w-6 text-green-600" />
                </div>
                <h3 className="mt-2 text-lg font-medium text-gray-900">Check your email</h3>
                <div className="mt-2 px-2">
                  <p className="text-sm text-gray-500">
                    We've sent password reset instructions to <span className="font-medium">{email}</span>.
                    Please check your inbox and follow the link to reset your password.
                  </p>
                </div>
                <div className="mt-5">
                  <Link to="/login">
                    <Button
                      type="button"
                      fullWidth
                      variant="outline"
                    >
                      Back to login
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          ) : (
            <div className="px-4 py-5 sm:p-6">
              {error && (
                <div className="mb-4">
                  <Alert
                    type="error"
                    message={error}
                    dismissible
                    onDismiss={() => setError(null)}
                  />
                </div>
              )}
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <Input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  label="Email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  leftIcon={<Mail className="h-5 w-5 text-gray-400" />}
                  placeholder="Enter your email address"
                />
                
                <Button
                  type="submit"
                  loading={loading}
                  disabled={loading}
                  fullWidth
                >
                  Send reset instructions
                </Button>
                
                <div className="flex items-center justify-center">
                  <div className="text-sm">
                    <Link to="/login" className="font-medium text-pink-600 hover:text-pink-500">
                      Back to login
                    </Link>
                  </div>
                </div>
              </form>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default ForgotPassword;